import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiErrorDetail } from "../lib/api";
import { translate } from "../lib/i18n";
import { PublicFooter } from "../components/PublicFooter";
import { ArrowRight, ShieldCheck, Users, Trophy, Scale, User, Eye, EyeOff } from "lucide-react";

const QUICK_LOGINS = [
  { label: "Admin (Enrico)", email: "enrico@puresports.com", password: "playmaker123" },
  { label: "Agent", email: "agent@playmaker.fc", password: "Agent@2026" },
  { label: "Tech director", email: "td@playmaker.fc", password: "TD@2026" },
  { label: "Head scout", email: "headscout@playmaker.fc", password: "Scout@2026" },
  { label: "Head coach", email: "coach@playmaker.fc", password: "Coach@2026" },
  { label: "Club doctor", email: "doctor@playmaker.fc", password: "Doctor@2026" },
  { label: "Player portal", email: "client@playmaker.fc", password: "Client@2026" },
];

const ROLE_ROWS = [
  {
    tag: "Agent",
    icon: Users,
    title: "Agents & intermediaries",
    body: "Manage clients, contracts, cases, financials, and legal in one place.",
  },
  {
    tag: "Club",
    icon: Trophy,
    title: "Technical directors & club reps",
    body: "Build your transfer window target map from any inbound source.",
  },
  {
    tag: "Legal",
    icon: Scale,
    title: "Legal teams & lawyers",
    body: "Access shared case documents securely without seeing sensitive financial data.",
  },
  {
    tag: "Client",
    icon: User,
    title: "Players",
    body: "View your own homepage, schedule, upcoming events, and active case status.",
  },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorDetail, setErrorDetail] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setErrorDetail("");
    setLoading(true);
    const cleanEmail = (email || "").trim().toLowerCase();
    const cleanPassword = (password || "").trim();
    if (!cleanEmail || !cleanPassword) {
      setError("Please enter both email and password.");
      setLoading(false);
      return;
    }
    try {
      const u = await login(cleanEmail, cleanPassword);
      const role = u?.role;
      let dest = "/dashboard";
      if (role === "player" || role === "guardian") dest = "/portal";
      else if (role === "tech_director") dest = "/td-home";
      else if (role === "head_scout" || role === "scout") dest = "/scouting-home";
      else if (["head_coach", "asst_coach", "club_doctor", "physio"].includes(role)) dest = "/staff-home";
      else if ((role === "agent" || role === "admin") && window.matchMedia("(max-width: 1023px)").matches) dest = "/today";
      navigate(dest);
    } catch (err) {
      const status = err?.response?.status;
      const detail = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setError(detail);
      setErrorDetail(
        `HTTP ${status ?? "?"} · email="${cleanEmail}" · pwd length=${cleanPassword.length}`
      );
      // eslint-disable-next-line no-console
      console.warn("[login] failed", { status, detail, email: cleanEmail });
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = (q) => {
    setEmail(q.email);
    setPassword(q.password);
    setError("");
    setErrorDetail("");
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left dark panel */}
      <div
        className="hidden lg:flex flex-col justify-between w-1/2 p-14 text-slate-200 relative overflow-hidden"
        style={{ background: "var(--pm-navy)" }}
      >
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(at 18% 22%, rgba(0, 230, 118, 0.22), transparent 55%), radial-gradient(at 88% 90%, rgba(59, 130, 246, 0.10), transparent 55%)",
          }}
        />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center font-display font-black text-xl"
              style={{ background: "var(--pm-green)", color: "#062E17" }}
            >
              P
            </div>
            <div className="font-display text-2xl font-black tracking-tight text-white">Playmaker</div>
          </div>
        </div>

        <div className="relative max-w-lg" data-testid="landing-hero">
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-emerald-400 mb-3">
            Football operations platform
          </div>
          <h2 className="font-display text-5xl font-black tracking-tight text-white leading-[1.02]">
            One platform.<br />
            Agents, clubs,<br />
            and <span style={{ color: "var(--pm-green)" }}>everyone</span><br />
            between them.
          </h2>
          <p className="mt-6 text-slate-400 text-base leading-relaxed">
            From representation contracts to transfer targets, from scouting intelligence to match-day
            itineraries — Playmaker connects every part of the football industry in a single operational
            environment.
          </p>

          <div className="mt-8 space-y-2.5" data-testid="role-rows">
            {ROLE_ROWS.map((r) => {
              const Icon = r.icon;
              return (
                <div
                  key={r.tag}
                  className="flex items-start gap-3 rounded-xl border border-emerald-900/40 bg-emerald-950/30 p-3.5"
                  data-testid={`role-row-${r.tag.toLowerCase()}`}
                >
                  <div className="w-8 h-8 rounded-lg border border-emerald-600/40 flex items-center justify-center shrink-0 text-emerald-300">
                    <Icon size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm text-white tracking-tight">{r.title}</div>
                    <div className="text-[12px] text-slate-400 leading-snug mt-0.5">{r.body}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-300 px-2.5 py-1 rounded-md border border-emerald-700/40 bg-emerald-900/30 shrink-0">
                    {r.tag}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-1 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-bold">
            <div className="flex items-center gap-2"><ShieldCheck size={12} className="text-emerald-400" /> Role-based access</div>
            <div className="flex items-center gap-2"><ShieldCheck size={12} className="text-emerald-400" /> Audit log</div>
            <div className="flex items-center gap-2"><ShieldCheck size={12} className="text-emerald-400" /> Encrypted at rest</div>
            <div className="flex items-center gap-2"><ShieldCheck size={12} className="text-emerald-400" /> GDPR compliant</div>
          </div>
        </div>

        <div className="relative text-xs text-slate-500">
          v3.0 · © Playmaker FC
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-14 bg-[#F4F6F8]">
        <div className="w-full max-w-md flex-1 flex flex-col justify-center">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center font-display font-black text-lg"
                 style={{ background: "var(--pm-green)", color: "#062E17" }}>P</div>
            <div className="font-display text-2xl font-black tracking-tight">Playmaker</div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Welcome back</div>
          <h1 className="font-display text-3xl lg:text-4xl font-black tracking-tight mt-1">Sign in to Playmaker</h1>
          <p className="text-slate-500 mt-2">Use a seeded role account to explore, or{" "}
            <Link to="/forgot-password" className="font-bold text-slate-500 hover:text-slate-800 mr-3" data-testid="link-forgot-password">{translate("Wachtwoord vergeten?")}</Link>
            <Link to="/register" className="font-bold text-emerald-700 hover:text-emerald-800" data-testid="link-to-register">
              create a new account
            </Link>.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form" autoComplete="off">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Email</label>
              <input
                className="pm-input mt-2"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@playmaker.fc"
                data-testid="login-email-input"
                autoComplete="off"
                name="pm-login-email"
                spellCheck={false}
                autoCapitalize="none"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Password</label>
              <div className="relative mt-2">
                <input
                  type={showPassword ? "text" : "password"}
                  className="pm-input pr-12"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password-input"
                  autoComplete="new-password"
                  name="pm-login-password"
                  spellCheck={false}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  data-testid="login-toggle-password-visibility"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg" data-testid="login-error">
                <div className="font-bold">{error}</div>
                {errorDetail && (
                  <div className="text-[11px] text-red-600 mt-1 font-mono break-all">{errorDetail}</div>
                )}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="pm-btn-primary w-full h-12 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="login-submit-button"
            >
              {loading ? "Signing in…" : (<>Sign in <ArrowRight size={16} /></>)}
            </button>
          </form>

          <div className="mt-8">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">
              Quick role login
            </div>
            <div className="grid grid-cols-2 gap-2" data-testid="quick-login-grid">
              {QUICK_LOGINS.map((q) => (
                <button
                  key={q.email}
                  type="button"
                  onClick={() => quickLogin(q)}
                  className="pm-btn-secondary h-10 rounded-xl px-4 text-xs font-bold"
                  data-testid={`quick-login-${q.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {q.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <PublicFooter className="w-full max-w-md mt-8 text-center" />
      </div>
    </div>
  );
}
